import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import logoUrl from "./assets/velyoo-genie-logo.svg";
import "./App.css";
import {
  getActiveAccount,
  initializeMsal,
  loginWithMicrosoft,
  logoutFromAddin,
} from "./auth";
import { Button } from "./components/Button";
import {
  FileExplorerWithPreview,
  type FileExplorerFilterGroup,
  type FileExplorerFilterOption,
  type FileExplorerPreviewItem,
} from "./compositions/FileExplorerWithPreview";
import {
  contextFromSession,
  fetchAuthSession,
  fetchContentFileSlides,
  fetchContentFilters,
  fetchTemplateFilters,
  getApiErrorMessage,
  searchContentFiles,
  searchTemplateSlides,
  type ApiContext,
  type ContentFile,
  type SlideResult,
} from "./services/apiClient";
import { powerPointService } from "./services/powerPointService";

type Tab = "templates" | "content";
type TemplateFilter = string;
type ContentFilter = string;

const defaultTemplateFilterOptions: FileExplorerFilterOption[] = [
  { label: "All Templates", value: "all" },
];

const PAGE_SIZE = 10;
const CONTENT_SEARCH_DEBOUNCE_MS = 1000;
const DEFAULT_CONTENT_FILE_SIZE_LIMIT_BYTES = 20 * 1024 * 1024;

function formatMegabytes(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getRenderablePreviewSrc(
  src: string | undefined,
  previewStatus: string | undefined,
) {
  if (!src || previewStatus === "pending" || previewStatus === "error") {
    return undefined;
  }

  return src;
}

function getContentFileSizeLimit(file: ContentFile) {
  return file.maxOpenFileSizeBytes ?? DEFAULT_CONTENT_FILE_SIZE_LIMIT_BYTES;
}

function isContentFileOverLimit(file: ContentFile) {
  return Boolean(
    file.fileSizeBytes && file.fileSizeBytes > getContentFileSizeLimit(file),
  );
}

function getContentFileDisabledReason(file: ContentFile) {
  if (file.disabledReason) {
    return file.disabledReason;
  }

  if (isContentFileOverLimit(file)) {
    return `File exceeds ${formatMegabytes(getContentFileSizeLimit(file))} limit.`;
  }

  if (file.canOpenSlides === false) {
    return "This file cannot be opened in the add-in.";
  }

  return undefined;
}

function isContentFileDisabled(file: ContentFile) {
  return file.canOpenSlides === false || isContentFileOverLimit(file);
}

function slideToPreviewItem(slide: SlideResult): FileExplorerPreviewItem {
  return {
    id: slide.id,
    imageAlt: slide.title,
    imageSrc: getRenderablePreviewSrc(slide.thumbnailUrl, slide.previewStatus),
    name: slide.title,
    previewStatus: slide.previewStatus,
  };
}

function fileToPreviewItem(file: ContentFile): FileExplorerPreviewItem {
  const disabledReason = getContentFileDisabledReason(file);
  const metaParts = [
    file.slideCount == null
      ? undefined
      : `${file.slideCount} slide${file.slideCount === 1 ? "" : "s"}`,
    file.fileSizeBytes ? formatMegabytes(file.fileSizeBytes) : undefined,
  ].filter(Boolean);

  return {
    disabled: isContentFileDisabled(file),
    disabledReason,
    id: file.id,
    imageAlt: file.name,
    imageSrc: getRenderablePreviewSrc(file.thumbnailUrl, file.previewStatus),
    metaText: metaParts.length ? metaParts.join(", ") : undefined,
    name: file.name,
    previewStatus: file.previewStatus,
  };
}

function Logo() {
  return (
    <div className="logo" aria-label="velyoo Genie">
      <img src={logoUrl} alt="velyoo Genie" />
    </div>
  );
}

function getPreviewRefreshDelayMs(
  items: Array<{ previewRetryAfterSeconds?: number; previewStatus: string }>,
) {
  const pendingItems = items.filter((item) => item.previewStatus === "pending");
  if (!pendingItems.length) {
    return null;
  }

  const retrySeconds = Math.min(
    ...pendingItems.map((item) =>
      Math.max(1, item.previewRetryAfterSeconds ?? 5),
    ),
  );
  return retrySeconds * 1000;
}

function clearLoadErrorStatus(status: string) {
  return status.startsWith("Could not load") ? "" : status;
}

function flattenTemplateFilterGroups(groups: FileExplorerFilterGroup[]) {
  return [
    ...defaultTemplateFilterOptions,
    ...groups.flatMap((group) => group.options),
  ];
}

function AppHeader({ onLogout }: { onLogout?: () => void }) {
  return (
    <header className="addin-header" data-node-id="11644:63355">
      <div className="header-logo-group">
        <Logo />
      </div>
      {onLogout ? (
        <Button
          ariaLabel="Log out"
          className="header-logout-button"
          iconName="logout"
          onClick={onLogout}
          size="small"
          style="chip"
          type="icon"
        />
      ) : null}
    </header>
  );
}

function Toggle({
  checked,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`toggle-field ${disabled ? "is-disabled" : ""}`}>
      <input
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      <span className="toggle-track" aria-hidden="true">
        <span className="toggle-knob" />
      </span>
      <span>{label}</span>
    </label>
  );
}

function LoginScreen({
  onAuthenticated,
}: {
  onAuthenticated: (diagnostic?: string) => void;
}) {
  const [error, setError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const login = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError("");
    setIsLoggingIn(true);

    try {
      await loginWithMicrosoft();
      onAuthenticated();
    } catch (nextError) {
      console.error("Microsoft login failed", nextError);
      setError(
        nextError instanceof Error ? nextError.message : "Sign in failed.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <main className="addin-shell login-shell" data-node-id="11648:33530">
      <AppHeader />
      <form className="login-card" onSubmit={login}>
        <div className="login-title">
          <h1>Welcome</h1>
          <p>Sign in to continue</p>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="login-actions">
          <Button
            htmlType="submit"
            iconName="right"
            leftIcon={false}
            state={isLoggingIn ? "disabled" : "default"}
            style="primary"
          >
            {isLoggingIn ? "Logging in" : "Log in"}
          </Button>
        </div>
      </form>
    </main>
  );
}

function Tabs({
  activeTab,
  onChange,
}: {
  activeTab: Tab;
  onChange: (tab: Tab) => void;
}) {
  return (
    <nav className="tabs" aria-label="Add content">
      <button
        className={activeTab === "templates" ? "is-active" : ""}
        onClick={() => onChange("templates")}
        type="button"
      >
        Templates
      </button>
      <button
        className={activeTab === "content" ? "is-active" : ""}
        onClick={() => onChange("content")}
        type="button"
      >
        Content
      </button>
    </nav>
  );
}

function TaskPane({
  authDiagnostic,
  onLogout,
}: {
  authDiagnostic: string;
  onLogout: () => void;
}) {
  const [activeTab, setActiveTab] = useState<Tab>("templates");
  const [query, setQuery] = useState("");
  const [debouncedContentQuery, setDebouncedContentQuery] = useState("");
  const [apiContext, setApiContext] = useState<ApiContext | null>(null);
  const [templateFilter, setTemplateFilter] = useState<TemplateFilter>("all");
  const [contentFilter, setContentFilter] = useState<ContentFilter>("");
  const [keepFormatting, setKeepFormatting] = useState(true);
  const [templateFilterOptions, setTemplateFilterOptions] = useState<
    FileExplorerFilterOption[]
  >(defaultTemplateFilterOptions);
  const [contentFilterGroups, setContentFilterGroups] = useState<
    FileExplorerFilterGroup[]
  >([]);
  const [templates, setTemplates] = useState<SlideResult[]>([]);
  const [files, setFiles] = useState<ContentFile[]>([]);
  const [selectedFileSlides, setSelectedFileSlides] = useState<SlideResult[]>(
    [],
  );
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingSelectedFileSlides, setIsLoadingSelectedFileSlides] =
    useState(false);
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const [isLoadingMoreFiles, setIsLoadingMoreFiles] = useState(false);
  const [isLoadingMoreSelectedFileSlides, setIsLoadingMoreSelectedFileSlides] =
    useState(false);
  const [hasMoreTemplates, setHasMoreTemplates] = useState(false);
  const [hasMoreFiles, setHasMoreFiles] = useState(false);
  const [hasMoreSelectedFileSlides, setHasMoreSelectedFileSlides] =
    useState(false);
  const [templateNextPage, setTemplateNextPage] = useState<
    number | undefined
  >();
  const [fileNextPage, setFileNextPage] = useState<number | undefined>();
  const [selectedFileSlideNextPage, setSelectedFileSlideNextPage] = useState<
    number | undefined
  >();
  const [selectedFile, setSelectedFile] = useState<ContentFile | null>(null);
  const [selectedSlideIds, setSelectedSlideIds] = useState<Set<string>>(
    new Set(),
  );
  const [status, setStatus] = useState("");
  const [isInserting, setIsInserting] = useState(false);
  const insertInFlightRef = useRef(false);
  const apiContextKey = apiContext
    ? `${apiContext.tenantUuid}:${apiContext.subtenantUuid}`
    : "";
  const selectedTemplateFilterOption = useMemo(
    () =>
      templateFilterOptions.find((option) => option.value === templateFilter),
    [templateFilter, templateFilterOptions],
  );
  const selectedTemplateFilterParams = selectedTemplateFilterOption?.params;
  const selectedContentFilterOption = useMemo(
    () =>
      contentFilterGroups
        .flatMap((group) => group.options)
        .find((option) => option.value === contentFilter),
    [contentFilter, contentFilterGroups],
  );
  const selectedContentFilterParams = selectedContentFilterOption?.params;

  useEffect(() => {
    let isCurrent = true;

    setIsLoadingSession(true);
    fetchAuthSession()
      .then((session) => {
        if (isCurrent) {
          setApiContext(contextFromSession(session));
          setStatus((current) => clearLoadErrorStatus(current));
        }
      })
      .catch((error) => {
        console.error("Auth session failed", error);
        if (isCurrent) {
          setApiContext(null);
          setTemplates([]);
          setFiles([]);
          setSelectedFileSlides([]);
          setStatus(
            getApiErrorMessage(error, "Could not load account context."),
          );
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingSession(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;

    if (!apiContext) {
      setTemplateFilterOptions(defaultTemplateFilterOptions);
      return () => {
        isCurrent = false;
      };
    }

    fetchTemplateFilters(apiContext)
      .then((groups) => {
        if (isCurrent) {
          const nextOptions = flattenTemplateFilterGroups(groups);
          setTemplateFilterOptions(nextOptions);
          setTemplateFilter((current) =>
            nextOptions.some((option) => option.value === current)
              ? current
              : "all",
          );
        }
      })
      .catch((error) => {
        console.error("Template filters failed", error);
        if (isCurrent) {
          setTemplateFilterOptions(defaultTemplateFilterOptions);
          setTemplateFilter("all");
          setStatus(getApiErrorMessage(error, "Could not load filters."));
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [apiContext, apiContextKey]);

  useEffect(() => {
    let isCurrent = true;

    if (!apiContext) {
      setContentFilterGroups([]);
      setContentFilter("");
      return () => {
        isCurrent = false;
      };
    }

    fetchContentFilters(apiContext)
      .then((groups) => {
        if (isCurrent) {
          setContentFilterGroups(groups);
          setContentFilter((current) =>
            !current ||
            groups
              .flatMap((group) => group.options)
              .some((option) => option.value === current)
              ? current
              : "",
          );
        }
      })
      .catch((error) => {
        console.error("Content filters failed", error);
        if (isCurrent) {
          setContentFilterGroups([]);
          setContentFilter("");
          setStatus(getApiErrorMessage(error, "Could not load filters."));
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [apiContext, apiContextKey]);

  const loadTemplatesPage = useCallback(
    async (page: number, mode: "append" | "replace" | "refresh") => {
      if (!apiContext) {
        setTemplates([]);
        setHasMoreTemplates(false);
        setTemplateNextPage(undefined);
        return;
      }

      if (mode === "append") {
        setIsLoadingMoreTemplates(true);
      } else if (mode === "replace") {
        setIsLoadingTemplates(true);
      }

      try {
        const result = await searchTemplateSlides(apiContext, {
          filterParams: selectedTemplateFilterParams,
          page,
          pageSize: PAGE_SIZE,
          query,
        });

        setTemplates((current) =>
          mode === "append" ? [...current, ...result.items] : result.items,
        );
        setHasMoreTemplates(result.hasMore);
        setTemplateNextPage(result.nextPage);
        setStatus((current) => clearLoadErrorStatus(current));
      } catch (error) {
        console.error("Template search failed", error);
        if (mode !== "append") {
          setTemplates([]);
          setHasMoreTemplates(false);
          setTemplateNextPage(undefined);
        }
        setStatus(getApiErrorMessage(error, "Could not load templates."));
      } finally {
        if (mode === "append") {
          setIsLoadingMoreTemplates(false);
        } else if (mode === "replace") {
          setIsLoadingTemplates(false);
        }
      }
    },
    [apiContext, query, selectedTemplateFilterParams],
  );

  const loadFilesPage = useCallback(
    async (page: number, mode: "append" | "replace" | "refresh") => {
      if (!apiContext) {
        setFiles([]);
        setHasMoreFiles(false);
        setFileNextPage(undefined);
        return;
      }

      if (mode === "append") {
        setIsLoadingMoreFiles(true);
      } else if (mode === "replace") {
        setIsLoadingFiles(true);
      }

      try {
        const result = await searchContentFiles(apiContext, {
          filterParams: selectedContentFilterParams,
          page,
          pageSize: PAGE_SIZE,
          query: debouncedContentQuery,
        });

        setFiles((current) =>
          mode === "append" ? [...current, ...result.items] : result.items,
        );
        setHasMoreFiles(result.hasMore);
        setFileNextPage(result.nextPage);
        setStatus((current) => clearLoadErrorStatus(current));
      } catch (error) {
        console.error("Content search failed", error);
        if (mode !== "append") {
          setFiles([]);
          setHasMoreFiles(false);
          setFileNextPage(undefined);
        }
        setStatus(getApiErrorMessage(error, "Could not load files."));
      } finally {
        if (mode === "append") {
          setIsLoadingMoreFiles(false);
        } else if (mode === "replace") {
          setIsLoadingFiles(false);
        }
      }
    },
    [apiContext, debouncedContentQuery, selectedContentFilterParams],
  );

  const loadSelectedFileSlidesPage = useCallback(
    async (page: number, mode: "append" | "replace" | "refresh") => {
      if (!apiContext || !selectedFile) {
        setSelectedFileSlides([]);
        setHasMoreSelectedFileSlides(false);
        setSelectedFileSlideNextPage(undefined);
        return;
      }

      if (mode === "append") {
        setIsLoadingMoreSelectedFileSlides(true);
      } else if (mode === "replace") {
        setIsLoadingSelectedFileSlides(true);
      }

      try {
        const result = await fetchContentFileSlides(apiContext, selectedFile, {
          page,
          pageSize: PAGE_SIZE,
        });

        setSelectedFileSlides((current) =>
          mode === "append" ? [...current, ...result.items] : result.items,
        );
        setHasMoreSelectedFileSlides(result.hasMore);
        setSelectedFileSlideNextPage(result.nextPage);
        setStatus((current) => clearLoadErrorStatus(current));
      } catch (error) {
        console.error("Selected file slides failed", error);
        if (mode !== "append") {
          setSelectedFileSlides([]);
          setHasMoreSelectedFileSlides(false);
          setSelectedFileSlideNextPage(undefined);
        }
        setStatus(getApiErrorMessage(error, "Could not load slides."));
      } finally {
        if (mode === "append") {
          setIsLoadingMoreSelectedFileSlides(false);
        } else if (mode === "replace") {
          setIsLoadingSelectedFileSlides(false);
        }
      }
    },
    [apiContext, selectedFile],
  );

  useEffect(() => {
    if (activeTab !== "content" || selectedFile) {
      return;
    }

    if (!query.trim()) {
      setDebouncedContentQuery("");
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedContentQuery(query);
    }, CONTENT_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, query, selectedFile]);

  useEffect(() => {
    if (!apiContext) {
      setTemplates([]);
      setHasMoreTemplates(false);
      setTemplateNextPage(undefined);
      return;
    }

    if (activeTab !== "templates") {
      return;
    }

    void loadTemplatesPage(1, "replace");
  }, [activeTab, apiContext, loadTemplatesPage]);

  useEffect(() => {
    if (!apiContext) {
      setFiles([]);
      setHasMoreFiles(false);
      setFileNextPage(undefined);
      return;
    }

    if (activeTab !== "content" || selectedFile) {
      return;
    }

    void loadFilesPage(1, "replace");
  }, [activeTab, apiContext, loadFilesPage, selectedFile]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedFileSlides([]);
      setHasMoreSelectedFileSlides(false);
      setSelectedFileSlideNextPage(undefined);
      return;
    }

    void loadSelectedFileSlidesPage(1, "replace");
  }, [loadSelectedFileSlidesPage, selectedFile]);

  useEffect(() => {
    setSelectedSlideIds(new Set());
  }, [activeTab, selectedFile?.id]);

  useEffect(() => {
    if (
      activeTab !== "templates" ||
      isLoadingTemplates ||
      isLoadingMoreTemplates
    ) {
      return;
    }

    const delay = getPreviewRefreshDelayMs(templates);
    if (!delay) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadTemplatesPage(1, "refresh");
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeTab,
    isLoadingMoreTemplates,
    isLoadingTemplates,
    loadTemplatesPage,
    templates,
  ]);

  useEffect(() => {
    if (
      activeTab !== "content" ||
      selectedFile ||
      isLoadingFiles ||
      isLoadingMoreFiles
    ) {
      return;
    }

    const delay = getPreviewRefreshDelayMs(files);
    if (!delay) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadFilesPage(1, "refresh");
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeTab,
    files,
    isLoadingFiles,
    isLoadingMoreFiles,
    loadFilesPage,
    selectedFile,
  ]);

  useEffect(() => {
    if (
      !selectedFile ||
      isLoadingSelectedFileSlides ||
      isLoadingMoreSelectedFileSlides
    ) {
      return;
    }

    const delay = getPreviewRefreshDelayMs(selectedFileSlides);
    if (!delay) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadSelectedFileSlidesPage(1, "refresh");
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [
    isLoadingMoreSelectedFileSlides,
    isLoadingSelectedFileSlides,
    loadSelectedFileSlidesPage,
    selectedFile,
    selectedFileSlides,
  ]);

  const loadMoreTemplates = useCallback(() => {
    if (
      !hasMoreTemplates ||
      !templateNextPage ||
      isLoadingTemplates ||
      isLoadingMoreTemplates
    ) {
      return;
    }

    void loadTemplatesPage(templateNextPage, "append");
  }, [
    hasMoreTemplates,
    isLoadingMoreTemplates,
    isLoadingTemplates,
    loadTemplatesPage,
    templateNextPage,
  ]);

  const loadMoreFiles = useCallback(() => {
    if (
      !hasMoreFiles ||
      !fileNextPage ||
      isLoadingFiles ||
      isLoadingMoreFiles
    ) {
      return;
    }

    void loadFilesPage(fileNextPage, "append");
  }, [
    fileNextPage,
    hasMoreFiles,
    isLoadingFiles,
    isLoadingMoreFiles,
    loadFilesPage,
  ]);

  const visibleSlides = useMemo(() => {
    if (activeTab === "templates") {
      return templates;
    }

    return selectedFile ? selectedFileSlides : [];
  }, [activeTab, selectedFile, selectedFileSlides, templates]);

  const selectedVisibleSlides = useMemo(
    () => visibleSlides.filter((slide) => selectedSlideIds.has(slide.id)),
    [selectedSlideIds, visibleSlides],
  );

  const loadMoreSelectedFileSlides = useCallback(() => {
    if (
      !hasMoreSelectedFileSlides ||
      !selectedFileSlideNextPage ||
      isLoadingSelectedFileSlides ||
      isLoadingMoreSelectedFileSlides
    ) {
      return;
    }

    void loadSelectedFileSlidesPage(selectedFileSlideNextPage, "append");
  }, [
    hasMoreSelectedFileSlides,
    isLoadingMoreSelectedFileSlides,
    isLoadingSelectedFileSlides,
    loadSelectedFileSlidesPage,
    selectedFileSlideNextPage,
  ]);

  const canInsert =
    Boolean(apiContext) && selectedVisibleSlides.length > 0 && !isInserting;

  const updateSelectedSlides = (slideIds: string[]) => {
    setSelectedSlideIds(new Set(slideIds));
  };

  const insertSelectedSlides = async () => {
    if (insertInFlightRef.current) {
      return;
    }

    const slides = selectedVisibleSlides;
    setStatus("");

    if (!apiContext) {
      setStatus("Could not load account context.");
      return;
    }

    if (!slides.length) {
      setStatus("Select a slide to insert.");
      return;
    }

    insertInFlightRef.current = true;
    setIsInserting(true);

    try {
      await powerPointService.insertSlides(slides, {
        context: apiContext,
        keepFormatting,
      });
      setStatus(
        `${slides.length} slide${slides.length === 1 ? "" : "s"} inserted.`,
      );
    } catch (error) {
      console.error("Slide insertion failed", error);
      setStatus(getApiErrorMessage(error, "Slide insertion failed."));
    } finally {
      insertInFlightRef.current = false;
      setIsInserting(false);
    }
  };

  return (
    <main
      className="addin-shell"
      data-node-id={
        activeTab === "templates"
          ? "11642:62288"
          : selectedFile
            ? "11648:33330"
            : "11648:32824"
      }
    >
      <AppHeader onLogout={onLogout} />
      <section className="content-stack">
        {authDiagnostic ? (
          <p className="auth-diagnostic">{authDiagnostic}</p>
        ) : null}
        <Tabs
          activeTab={activeTab}
          onChange={(tab) => {
            setActiveTab(tab);
            setSelectedFile(null);
            setQuery("");
            setDebouncedContentQuery("");
            setStatus("");
          }}
        />

        {activeTab === "templates" ? (
          <FileExplorerWithPreview
            contextText="Select several pages while holding the ctrl key"
            emptyMessage="No matching templates found."
            filterOptions={templateFilterOptions}
            filterValue={templateFilter}
            items={templates.map(slideToPreviewItem)}
            loading={isLoadingSession || isLoadingTemplates}
            loadingMore={isLoadingMoreTemplates}
            mode="full"
            multiSelect
            onEndReached={hasMoreTemplates ? loadMoreTemplates : undefined}
            onFilterChange={(value) =>
              setTemplateFilter(value as TemplateFilter)
            }
            onQueryChange={setQuery}
            onSelectionChange={updateSelectedSlides}
            query={query}
            selectedIds={[...selectedSlideIds]}
            showHeader={false}
          />
        ) : selectedFile ? (
          <div className="selected-file-view">
            <div className="selected-file">
              <Button
                ariaLabel="Back to files"
                iconName="left"
                onClick={() => setSelectedFile(null)}
                size="large"
                style="chip"
                type="icon"
              />
              <div className="selected-file-card">
                {getRenderablePreviewSrc(
                  selectedFile.thumbnailUrl,
                  selectedFile.previewStatus,
                ) ? (
                  <img
                    src={getRenderablePreviewSrc(
                      selectedFile.thumbnailUrl,
                      selectedFile.previewStatus,
                    )}
                    alt=""
                    draggable={false}
                    onDragStart={(event) => event.preventDefault()}
                  />
                ) : (
                  <span
                    className={[
                      "selected-file-card-preview",
                      selectedFile.previewStatus === "pending"
                        ? "is-loading"
                        : "",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                )}
                <span>{selectedFile.name}</span>
              </div>
            </div>
            <FileExplorerWithPreview
              contextText="Context"
              emptyMessage="No pages found."
              items={visibleSlides.map(slideToPreviewItem)}
              loading={isLoadingSelectedFileSlides}
              loadingMore={isLoadingMoreSelectedFileSlides}
              mode="full"
              multiSelect
              onEndReached={
                hasMoreSelectedFileSlides
                  ? loadMoreSelectedFileSlides
                  : undefined
              }
              onQueryChange={setQuery}
              onSelectionChange={updateSelectedSlides}
              query={query}
              selectedIds={[...selectedSlideIds]}
              showControls={false}
              showHeader={false}
            />
            <p className="instruction">
              Select several pages while holding the ctrl key
            </p>
          </div>
        ) : (
          <FileExplorerWithPreview
            contextText="Click to select a file"
            emptyMessage="No matching files found."
            filterGroups={contentFilterGroups}
            filterLabel="Category"
            filterValue={contentFilter}
            items={files.map(fileToPreviewItem)}
            loading={isLoadingFiles}
            loadingMore={isLoadingMoreFiles}
            onEndReached={hasMoreFiles ? loadMoreFiles : undefined}
            onFilterChange={(value) => setContentFilter(value as ContentFilter)}
            onQueryChange={setQuery}
            onSelectionChange={(ids) => {
              const nextFile = files.find((file) => file.id === ids[0]);
              if (nextFile && isContentFileDisabled(nextFile)) {
                return;
              }

              setSelectedFile(nextFile ?? null);
            }}
            query={query}
            selectedIds={[]}
            showHeader={false}
          />
        )}
      </section>
      <footer className="addin-footer">
        <Toggle
          checked={keepFormatting}
          disabled={!canInsert}
          label="Keep Formatting"
          onChange={setKeepFormatting}
        />
        <Button
          leftIcon={false}
          onClick={insertSelectedSlides}
          rightIcon={false}
          state={canInsert ? "default" : "disabled"}
          style="primary"
        >
          Insert
        </Button>
      </footer>
      {status || isLoadingSession ? (
        <p className="status-line">{status || "Loading account context."}</p>
      ) : null}
    </main>
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(getActiveAccount()),
  );
  const [isBootstrappingAuth, setIsBootstrappingAuth] = useState(true);
  const [authDiagnostic, setAuthDiagnostic] = useState("");

  useEffect(() => {
    let isMounted = true;

    initializeMsal()
      .then((account) => {
        if (isMounted) {
          setIsAuthenticated(Boolean(account));
        }
      })
      .catch((error) => {
        console.error("MSAL initialization failed", error);
      })
      .finally(() => {
        if (isMounted) {
          setIsBootstrappingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (isBootstrappingAuth) {
    return (
      <main className="addin-shell login-shell">
        <AppHeader />
        <section className="login-card">
          <div className="login-title">
            <h1>Welcome</h1>
            <p>Preparing sign in</p>
          </div>
        </section>
      </main>
    );
  }

  return isAuthenticated ? (
    <TaskPane
      authDiagnostic={authDiagnostic}
      onLogout={() => {
        logoutFromAddin()
          .catch((error) => {
            console.error("Logout failed", error);
          })
          .finally(() => {
            setAuthDiagnostic("");
            setIsAuthenticated(false);
          });
      }}
    />
  ) : (
    <LoginScreen
      onAuthenticated={(diagnostic) => {
        setAuthDiagnostic(diagnostic ?? "");
        setIsAuthenticated(true);
      }}
    />
  );
}
